import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { serverErrorMessage } from 'containers/SendOffer/errors';
import { useDraftApprovalPlan } from 'containers/SendOffer/hooks/useDraftApprovalPlan';
import {
  useSendHeldOffer,
  useSubmitOffer,
  useUpdateOffer,
} from 'containers/SendOffer/hooks/useOfferActions';
import { useOfferLetterForm } from 'containers/SendOffer/hooks/useOfferLetterForm';
import { useOfferPreview } from 'containers/SendOffer/hooks/useOfferPreview';
import { normalizeForm, normalizePreview } from 'containers/SendOffer/normalize';
import {
  EditedKeys,
  OfferLetterFormData,
  OfferStatus,
  PreviewResult,
} from 'containers/SendOffer/types';
import {
  editedKeys,
  effectiveConditionValues,
  effectiveValues,
} from 'containers/SendOffer/values';

import messages from './messages';
import { toApprovalPlan } from './normalize';
import { ApprovalPlan } from './types';

/** What a caller passes; every value comes off the request payload. */
interface UseOfferEditArgs {
  /** The applicant the offer is for. Null on a request with no offer summary. */
  applicantId: string | null;
  /** The template the offer under review was generated from. */
  templateId: string | null;
  /** The offer's own external_id — `subject_id` on the request payload. */
  offerId: string | null;
  /**
   * What the offer currently says, in both key spaces. The form opens on these
   * rather than on the template's own prefill — see the note on `seeded` below.
   */
  storedValues: Record<string, string>;
  storedConditionValues: Record<string, string>;
  /** Whether the form should be fetched at all. False outside the edit flow. */
  enabled: boolean;
  /** The edit landed and the letter has been re-rendered. */
  onPreviewed: () => void;
  /**
   * The submit landed, so the request the panel opened is closed: superseded
   * by a new one or released. Fires before any send a held offer chains, and
   * whatever that send does next.
   */
  onRequestClosed: () => void;
  /**
   * The offer went back through the approval rules.
   *
   * @param nextRequestId the request now deciding it, or null when no approver
   *   is left to ask: the offer is on its way to the applicant, or its stage
   *   holds it for a recruiter to send.
   * @param offerStatus the offer's status after the submit, which says which.
   */
  onSubmitted: (
    nextRequestId: string | null,
    offerStatus: OfferStatus | null,
  ) => void;
}

export interface OfferEdit {
  form: OfferLetterFormData | undefined;
  isLoading: boolean;
  isError: boolean;
  values: Record<string, string>;
  conditionValues: Record<string, string>;
  setField: (key: string, value: string) => void;
  setCondition: (dataKey: string, value: string) => void;
  /** The last readiness result — per-field errors and the count above the CTA. */
  readiness: PreviewResult | undefined;
  /** The server's own sentence for a refused write, verbatim where there is one. */
  error: string | undefined;
  /** A readiness check or a write is in flight. */
  busy: boolean;
  /** A write is in flight, so the whole form is inert. See `locked` below. */
  locked: boolean;
  /**
   * What a submit of the saved edit would do: the server's answer on the save
   * that produced the preview, or on a submit it refused because that answer
   * had gone stale. Null until the edit is saved.
   */
  plan: ApprovalPlan | null;
  /**
   * What a submit would do if the values on the form were saved as they stand,
   * asked while the edit is unsaved. Null while a change is waiting to be
   * asked about, and when the question failed: saying nothing beats naming
   * approvals for values the person has since replaced.
   */
  draftPlan: ApprovalPlan | null;
  /**
   * The values on the form are waiting for an answer: from the form's load or a
   * change, through the pause, until the answer or its failure lands.
   * `draftPlan` is null throughout.
   */
  draftPlanPending: boolean;
  /** Runs readiness and, when it clears, saves and re-renders the letter. */
  preview: () => void;
  /**
   * Carries out what the plan names: sends the edited offer to the approvers it
   * needs, or to the applicant, including the send its stage would otherwise
   * hold for a recruiter.
   */
  submit: () => void;
  /** Drops the entered values and any error, so a reopened form starts clean. */
  reset: () => void;
}

/**
 * The offer form behind the review panel's Edit offer / Revise & resubmit
 * (AXHE-4759).
 *
 * ## It drives the recruiter's own endpoints, not new ones
 *
 * The write path is `PATCH internal_api/offer_letters/:id` followed by
 * `POST .../submit_for_approval` — exactly what `containers/SendOffer` calls,
 * with exactly the payload `SendOffer/values.ts` resolves. A panel-local edit
 * endpoint would be a second implementation of "what does an edited offer mean",
 * and the two would diverge on the first change to either.
 *
 * ## Preview is the commit point, and the banner is what discloses it
 *
 * `PATCH` is what re-renders the letter, so the panel cannot show a revised
 * document without writing one. On an offer that is still under approval the
 * same write demotes it to `draft` and cancels its open request
 * (`OfferLetters::UpdatePreview`) — which is precisely what the form's warning
 * banner says will happen, and why the banner is shown before this runs rather
 * than at submit.
 *
 * So the boundary is: everything before **Preview new offer** is local and
 * discardable; everything after it has already happened. A rejected offer is the
 * benign case — it is already `draft` and its request is already `rejected`, so
 * the write changes values and nothing else.
 *
 * ## The form opens on the offer, not on the template
 *
 * `values` is seeded from the request payload rather than left to the form
 * endpoint's own prefill. That endpoint pre-fills from
 * `OfferLetters::FindResumableOffer`, which deliberately excludes
 * `pending_approval` and `approved` offers — reopening the recruiter's form must
 * not cancel a live approval as a side effect. Those two statuses are exactly
 * what an approver edits from here, so without the seed the form would ask them
 * to retype an offer that already has values.
 *
 * ## Readiness runs first, deliberately
 *
 * The same check the Send Offer form runs before its own create/update. It costs
 * a round trip, and it buys the one thing that matters here: an offer with an
 * unresolvable merge field is refused BEFORE the write that would cancel a live
 * approval request. Without it the approver learns the letter cannot be
 * generated only after the approvals have been thrown away.
 */
export const useOfferEdit = ({
  applicantId,
  templateId,
  offerId,
  storedValues,
  storedConditionValues,
  enabled,
  onPreviewed,
  onRequestClosed,
  onSubmitted,
}: UseOfferEditArgs): OfferEdit => {
  const intl = useIntl();
  // What the reader has CHANGED, not what the form holds. The offer's own values
  // are layered underneath at read time rather than seeded into state: the panel
  // mounts this hook before the request resolves, so a seeded initial state
  // would be seeded from an empty payload and never corrected — and an effect
  // that re-seeded on arrival would clobber whatever had been typed by then.
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [conditionEdits, setConditionEdits] = useState<Record<string, string>>(
    {},
  );
  const [error, setError] = useState<string | undefined>();
  const [plan, setPlan] = useState<ApprovalPlan | null>(null);
  // The submit has parked the offer at `ready_to_send` and its send has not
  // gone through, so pressing the button again retries the send alone: a
  // second submit of a held offer is refused.
  const [held, setHeld] = useState(false);

  // An entry the reader cleared is an edit, and `''` correctly wins over the
  // stored answer — which is why this is a spread and not a `??` per key.
  const values = useMemo(
    () => ({ ...storedValues, ...edits }),
    [edits, storedValues],
  );
  const conditionValues = useMemo(
    () => ({ ...storedConditionValues, ...conditionEdits }),
    [conditionEdits, storedConditionValues],
  );

  const { result: formResult } = useOfferLetterForm(
    applicantId ?? '',
    templateId ?? undefined,
    // Never fires until an approver opens the form: the endpoint 403s an
    // approver with no visibility on the applicant, and every panel open would
    // otherwise carry one failing request for a form nobody asked for.
    { enabled: enabled && Boolean(applicantId) },
  );
  // `reloading` carries data too, and reading only `ready` is why this is
  // spelled out: re-entering the form after a Cancel re-runs the fetch (the
  // `enabled` flag is a dependency of it), which parks the result at `reloading`
  // — a state that is neither `ready` nor `error`, so a `ready`-only read hands
  // the form back `undefined` and the panel renders its load-failure message
  // over a form that loaded perfectly well.
  const form = useMemo(
    () =>
      formResult.status === 'ready' || formResult.status === 'reloading'
        ? normalizeForm(formResult.data)
        : undefined,
    [formResult],
  );

  // The same payload the save would send.
  const draftPayload = useMemo(
    () =>
      form
        ? {
            field_values: effectiveValues(form, values),
            condition_values: effectiveConditionValues(form, conditionValues),
          }
        : null,
    [form, values, conditionValues],
  );
  const { plan: draftPlan, pending: draftPlanPending } = useDraftApprovalPlan(
    offerId,
    draftPayload,
    enabled,
  );

  // Cancels an in-flight readiness check. Without it a check that resolves after
  // an edit clobbers the reset state back to a stale "ready" — the same guard
  // SendOffer's container carries, and for the same reason.
  const cancelPreview = useRef<(() => void) | undefined>(undefined);
  // The exact payload a readiness check was run against, captured at click time
  // so the write that follows a passing check uses the values the check vouched
  // for rather than whatever is on screen when the response lands.
  const capturedPayload = useRef<
    | {
        field_values: Record<string, string>;
        condition_values: Record<string, string>;
        edited_keys: EditedKeys;
      }
    | undefined
  >(undefined);

  useEffect(() => () => cancelPreview.current?.(), []);

  // Every refusal below relays the server's own sentence, verbatim where there
  // is one. Each names a different remedy — the offer is already sent, a merge
  // field will not resolve, the document is an upload and values cannot change
  // it, this user cannot manage the applicant — and collapsing them into one
  // sentence leaves the reader retrying something retrying cannot fix. The
  // fallbacks are for a failure that carried no body at all: a network error, a
  // timeout, a 500 with no JSON.
  const update = useUpdateOffer(offerId ?? '', {
    onSuccess: data => {
      setError(undefined);
      setHeld(false);
      setPlan(
        toApprovalPlan(
          (data as { approval?: { plan?: unknown } })?.approval?.plan,
        ),
      );
      onPreviewed();
    },
    onError: body =>
      setError(
        serverErrorMessage(body) ?? intl.formatMessage(messages.previewFailed),
      ),
  });

  // "Send to applicant" on a stage that holds approved offers. The submit parks
  // the offer for a recruiter, and the person pressing the button is that
  // recruiter, so this send finishes what the button named in the same click.
  const sendHeld = useSendHeldOffer(offerId ?? '', {
    onSuccess: data => {
      setError(undefined);
      setHeld(false);
      onSubmitted(null, data?.status ?? null);
    },
    onError: body =>
      setError(
        serverErrorMessage(body) ??
          intl.formatMessage(messages.sendToApplicantFailed),
      ),
  });

  const submitOffer = useSubmitOffer(offerId ?? '', {
    onSuccess: data => {
      setError(undefined);
      // The edits are the server's now. Dropped here rather than by the caller,
      // because this hook outlives the request it was editing — the panel swaps
      // itself onto the new request without remounting — and a kept edit would
      // sit on top of the values that same edit has just persisted, winning over
      // whatever the server made of them on the next open.
      setEdits({});
      setConditionEdits({});
      // Told now rather than when a held offer's send lands: a failed send leaves
      // the drawer open on an error, and closing it from there would otherwise
      // leave the queue listing the request this submit just closed.
      onRequestClosed();
      // The resubmission raises a NEW request with a new external_id, which is
      // what the panel is addressed by. Null means no approver is left to ask.
      const nextId = (data as { approval?: { request_id?: string | null } })
        ?.approval?.request_id;
      const status = data?.status ?? null;
      // The digest held the submit to the plan on screen, so a hold here is
      // the hold the button already named as a send. The status alone is not
      // enough: with no plan the button named approval, and a held offer
      // stays with its recruiter.
      if (plan?.outcome === 'hold' && status === 'ready_to_send') {
        setHeld(true);
        sendHeld.mutation();
        return;
      }
      onSubmitted(typeof nextId === 'string' && nextId ? nextId : null, status);
    },
    onError: body => {
      // A 409: the submit would have routed the offer differently from the
      // preview on screen, so the server refused it and sent the offer as it
      // stands. Redrawing from that answer is what keeps the person from
      // pressing the button again on the stale one.
      const fresh = (body as { preview?: { approval?: { plan?: unknown } } })
        ?.preview?.approval;
      if (fresh) setPlan(toApprovalPlan(fresh.plan));
      setError(
        serverErrorMessage(body) ?? intl.formatMessage(messages.submitFailed),
      );
    },
  });

  const {
    mutation: runPreview,
    result: previewResult,
    reset: resetPreview,
  } = useOfferPreview(applicantId ?? '', {
    onSuccess: data => {
      const payload = capturedPayload.current;
      capturedPayload.current = undefined;
      // A stray resolve must not fall through to a write with an empty payload.
      if (!payload) return;
      // A failed check leaves the approver on the form. `previewResult` is
      // already `ready` by now, so the per-field errors and the count render
      // from it with no further action here — and no write has happened, which
      // is the whole point of checking first.
      if (normalizePreview(data).can_generate) update.mutation(payload);
    },
    onError: body => {
      capturedPayload.current = undefined;
      setError(
        serverErrorMessage(body) ?? intl.formatMessage(messages.previewFailed),
      );
    },
  });

  const readiness = useMemo(
    () => (previewResult.status === 'ready' ? normalizePreview(previewResult.data) : undefined),
    [previewResult],
  );

  // Any edit invalidates the last check: a passing result only vouches for the
  // values it ran against. The in-flight request is cancelled too, so a slow
  // response cannot resolve and write values that are no longer on screen —
  // `useApiServiceMutation` calls neither callback on a cancel.
  const invalidate = useCallback(() => {
    cancelPreview.current?.();
    cancelPreview.current = undefined;
    capturedPayload.current = undefined;
    if (previewResult.status !== 'idle') resetPreview();
  }, [previewResult.status, resetPreview]);

  const setField = useCallback(
    (key: string, value: string) => {
      setEdits(current => ({ ...current, [key]: value }));
      setError(undefined);
      invalidate();
    },
    [invalidate],
  );

  const setCondition = useCallback(
    (dataKey: string, value: string) => {
      setConditionEdits(current => ({ ...current, [dataKey]: value }));
      setError(undefined);
      invalidate();
    },
    [invalidate],
  );

  const preview = () => {
    const submitted = {
      field_values: effectiveValues(form, values),
      condition_values: effectiveConditionValues(form, conditionValues),
    };
    // AXHE-4673, and the baseline is the load-bearing part. This panel renders
    // the OFFER's stored answers, not the form payload's prefill —
    // FindResumableOffer excludes `pending_approval` and `approved`, so the form
    // endpoint hands back the template's prefill and `values` layers the stored
    // answers over it. Comparing against `field.value` would report every
    // control whose stored answer has since diverged from a fresh prefill, and
    // an ordinary edit to the applicant record moves that prefill on its own —
    // so an untouched control would be reported as changed and would then
    // overwrite the applicant's newer value with the offer's older one.
    //
    // This session's own edits are all this reports. What an earlier session
    // changed is already on the offer; the server unions the two.
    capturedPayload.current = {
      ...submitted,
      edited_keys: editedKeys(form, values, conditionValues, {
        field_values: storedValues,
        condition_values: storedConditionValues,
      }),
    };
    setError(undefined);
    // Readiness is asked about the values only — that endpoint neither permits
    // the report nor writes anything.
    cancelPreview.current = runPreview({
      offer_letter_template_id: templateId ?? undefined,
      ...submitted,
    });
  };

  // Drops the edits; the offer's own values stay, since they are the payload's.
  const reset = useCallback(() => {
    setEdits({});
    setConditionEdits({});
    setError(undefined);
    setPlan(null);
    setHeld(false);
    invalidate();
  }, [invalidate]);

  const locked =
    update.result.status === 'loading' ||
    submitOffer.result.status === 'loading' ||
    sendHeld.result.status === 'loading';

  return {
    form,
    // The state's own flags, not a status comparison: `isLoading` is true for
    // `reloading` as well as `loading`, which is what a re-entered form is in.
    isLoading: formResult.isLoading,
    isError: formResult.isError,
    values,
    conditionValues,
    setField,
    setCondition,
    readiness,
    error,
    busy: locked || previewResult.status === 'loading',
    locked,
    plan,
    draftPlan,
    draftPlanPending,
    preview,
    submit: () => {
      setError(undefined);
      if (held) sendHeld.mutation();
      else submitOffer.mutation(plan?.digest);
    },
    reset,
  };
};
